import { TypeOfConsideration } from '../../slices/marketplace/pricing/models/type-of-consideration.enum';
import { MarketplaceParty, UploadedDocument } from './flow-of-funds.model';

export interface DeferredPaymentSchedule {
    engagementId: number;
    summary: DeferredPaymentSummary;
    sections: DeferredPaymentSection[];
    totalProjectedAmount: number;
    totalProjectedAdvisoryFee: number;
}

/* ─────────────────── Summary ledger ─────────────────── */

export interface DeferredPaymentSummary {
    totalProjectedDealValueAtClose: number;
    portionReceivedAtClose: number;
    feesPaidAtClose: number;
    totalProjectedDeferredPayments: number;
    totalReceivedDeferredPortion: number;
    feesPaidOnReceivedDeferredPayments: number;
    totalProjectedDeferredPaymentsRemaining: number;
    projectedFeesOnRemainingPayments: number;
}

/* ─────────────────── Sections ─────────────────── */

export interface DeferredPaymentSection {
    considerationType: TypeOfConsideration;
    label: string;
    terms: DeferredPaymentTerms;
    payments: DeferredPayment[];
    subtotalProjectedAmount: number;
    subtotalProjectedAdvisoryFee: number;
}

export interface DeferredPaymentTerms {
    buyerCalculationDays: number;
    sellerResponseDays: number;
    paymentDueDays: number;
}

export enum DeferredPaymentStatus {
    Scheduled = 0,
    Upcoming = 1,
    PaymentScheduled = 2,
    PaymentSent = 3,
    PaymentPending = 4,
    Paid = 5,
    Overpayment = 6,
    Underpayment = 7,
    DifferentFromAgreedAmount = 8,
    ReStructured = 9,
}

export interface DeferredPayment {
    id: number;
    label: string;
    basis: string;
    paymentProcessStartDate: string | null; // ISO date
    isStartDateEventDriven: boolean;
    projectedAmount: number;
    projectedAdvisoryFee: number;
    interestAccrued: number | null;
    calculationNote: string | null;
    equityClass: EquityClass | null;
    equityTermsSummary: string | null;
    calculation: CalculationThread;
    sentConfirmation: PaymentConfirmation;
    receivedConfirmation: PaymentConfirmation;
    statuses: DeferredPaymentStatus[];
    reminderPreferences: ReminderPreferences;
    replacements: DeferredPayment[];
}

/* ─────────────────── Calculation negotiation ─────────────────── */

export interface CalculationThread {
    entries: CalculationEntry[];
    isResolved: boolean;
    resolvedAmount: number | null;
    resolvedAdvisoryFee: number | null;
}

export enum CalculationEntryKind {
    Proposal = 0,
    Counter = 1,
    Dispute = 2,
    Agree = 3,
}

/** Common vs Preferred Corp Equity — the two equity classes a deferred
 *  payment can badge. */
export enum EquityClass {
    Common = 0,
    Preferred = 1,
}

export interface CalculationEntry {
    id: number;
    party: MarketplaceParty;
    kind: CalculationEntryKind;
    amount: number | null;
    note: string | null;
    documents: UploadedDocument[];
    createdOn: string; // ISO date-time
}

/* ─────────────────── Payment confirmation ─────────────────── */

/** NotApplicable — the payment is not yet in a confirmable state.
 *  AwaitingAction — this party still has to confirm.
 *  Confirmed — recorded, with the amount actually moved. */
export enum PaymentConfirmationState {
    NotApplicable = 0,
    AwaitingAction = 1,
    Confirmed = 2,
}

export interface PaymentConfirmation {
    state: PaymentConfirmationState;
    amount: number | null;
    confirmedOn: string | null; // ISO date-time
    documents: UploadedDocument[];
}

/* ─────────────────── Reminders ─────────────────── */

export interface ReminderPreferences {
    monthPrior: boolean;
    weekPrior: boolean;
    onDueDate: boolean;
    whenOtherPartyActs: boolean;
}
